import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { HelmetProvider } from 'react-helmet-async';
import ReactGA from 'react-ga4';
import { gtmManager } from './lib/gtm';
import { usePageTracking } from './hooks/usePageTracking';
import { GTM_CONFIG } from './config/gtm-config';
import QuoteTableList from './pages/QuoteTableList';
import LandingPage from './pages/LandingPage';
import AboutPage from './pages/AboutPage';
import OverviewPage from './pages/OverviewPage';
import AddQuotePage from './pages/AddQuotePage';
import SendQuoteStylePage from './pages/SendQuoteStylePage';
import SendQuoteCardPage from './pages/SendQuoteCardPage';
import QuoteSelectionPage from './pages/QuoteSelectionPage';
import EarlyAccessPage from './pages/EarlyAccessPage';
import DemoPage from './pages/DemoPage';
import ApiLandingPage from './pages/ApiLandingPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import BatchUploadPage from './components/BatchUploadPage.tsx';
import AuthForm from './components/AuthForm';
import { useAuth } from './lib/AuthContext';

// Initialize Google Analytics and Google Ads
ReactGA.initialize([
  {
    trackingId: import.meta.env.VITE_GA_MEASUREMENT_ID,
    gtagConfig: {
      custom_map: { custom_parameter: 'custom_value' }
    }
  },
  {
    trackingId: import.meta.env.VITE_GA_ADS_CONVERSION_ID,
    gtagConfig: {
      groups: 'default'
    }
  }
]);

// Route change tracking component
function RouteTracker() {
  const location = useLocation();

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: location.pathname });
  }, [location]);

  return null;
}

function App() {
  const { user, loading: authLoading } = useAuth();

  useEffect(() => {
    if (GTM_CONFIG.enabled && GTM_CONFIG.gtmId) {
      gtmManager.initialize({
        gtmId: GTM_CONFIG.gtmId,
        dataLayerName: GTM_CONFIG.dataLayerName,
      });
    }
  }, []);

  usePageTracking();

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <HelmetProvider>
      <GoogleReCaptchaProvider reCaptchaKey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}>
        <Router>
          <RouteTracker />
          <Routes>
            <Route path="/" element={user ? <QuoteSelectionPage /> : <LandingPage />} />
            <Route path="/quotes" element={user ? <QuoteTableList /> : <Navigate to="/login" replace />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/overview" element={<OverviewPage />} />
            <Route path="/demo" element={<DemoPage />} />
            <Route path="/early-access" element={<EarlyAccessPage />} />
            <Route path="/api" element={<ApiLandingPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/login" element={user ? <Navigate to="/" replace /> : <AuthForm />} />
            <Route path="/add" element={user ? <AddQuotePage /> : <Navigate to="/login" replace />} />
            <Route path="/batch-upload" element={user ? <BatchUploadPage /> : <Navigate to="/login" replace />} />
            <Route path="/send/style/:id" element={user ? <SendQuoteStylePage /> : <Navigate to="/login" replace />} />
            <Route path="/send/card/:id" element={user ? <SendQuoteCardPage /> : <Navigate to="/login" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </GoogleReCaptchaProvider>
    </HelmetProvider>
  );
}

export default App;
